(() => {
  'use strict';

  const ACCESS_TTL_SECONDS = 86400;
  const ACCESS_SCOPE_BY_DIGEST = Object.freeze({
    '7091cfa1b74161b1350c91bd7062025fbdcffaa04910d581057f896dc64cde7c': 'ncaa',
    'ad61947acf6c3871efc0d622268bd4eab753f91af55ca7c7210a1804555447a7': 'nfl',
    '2ca54a262506be45522a942071e5303e163ccd391e2c254397ca18ce38b4ac5f': 'nba',
    '99cbc6a5ab0dd20529868caaaeaedec9290e7e683ff34a2b797e825a2b1ee597': 'wnba',
    'd15f96a9323388286f8c3e12fcc8d141e5bfff76aed8697d5112c05915e2b415': 'mlb',
    'a0df6272d81be4a45e86ae20bd1b92f271dc41348cbfce8db2c5f4f207e43c66': 'nhl',
    '6a3b3c571bdcc24ce4bebc8da6ea15c5bbc1af5ddcb98d0796e072168315556e': 'mls',
    '838020bf28a4c21178447cd456ad4e2106ca45837c69cfe1ff99e84b2fb4104a': 'all'
  });

  const gate = document.getElementById('LicensedTimeAccessGate');
  const storefront = document.getElementById('LicensedTimeStorefront');
  if (!gate || !storefront) return;

  const form = document.getElementById('LicensedTimeAccessForm');
  const codeInput = document.getElementById('LicensedTimeAccessCode');
  const errorMessage = document.getElementById('LicensedTimeAccessGateError');
  const submitButton = gate.querySelector('[data-lt-access-submit]');
  const publicButton = gate.querySelector('[data-lt-access-public]');
  const closeButton = gate.querySelector('[data-lt-access-close]');
  const pageStartedAt = performance.now();
  let returnFocus = null;
  let accessChannel = null;

  if ('BroadcastChannel' in window) {
    try {
      accessChannel = new BroadcastChannel('licensedtime-access');
      accessChannel.addEventListener('message', () => window.location.reload());
    } catch (error) {
      accessChannel = null;
      console.warn('Licensed Time cross-tab access updates are unavailable.', error);
    }
  }

  function setStorefrontInert(inert) {
    if (inert) {
      storefront.setAttribute('inert', '');
      storefront.setAttribute('aria-hidden', 'true');
    } else {
      storefront.removeAttribute('inert');
      storefront.removeAttribute('aria-hidden');
    }
  }

  function clearError() {
    errorMessage.hidden = true;
    errorMessage.textContent = '';
    codeInput.removeAttribute('aria-invalid');
  }

  function showError(message) {
    errorMessage.textContent = message;
    errorMessage.hidden = false;
    codeInput.setAttribute('aria-invalid', 'true');
    codeInput.focus({ preventScroll: true });
  }

  function focusableElements() {
    return [...gate.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), summary')]
      .filter((element) => !element.hidden && element.getClientRects().length > 0);
  }

  function handleGateKeydown(event) {
    if (event.key === 'Escape') {
      if (gate.dataset.ltAccessInitialRequired !== 'true') {
        event.preventDefault();
        closeGate();
      }
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = focusableElements();
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function openGate({ initialRequired = false, trigger = null } = {}) {
    returnFocus = trigger || document.activeElement;
    if (returnFocus instanceof HTMLElement && returnFocus.hasAttribute('aria-expanded')) {
      returnFocus.setAttribute('aria-expanded', 'true');
    }
    gate.dataset.ltAccessInitialRequired = initialRequired ? 'true' : 'false';
    gate.hidden = false;
    document.documentElement.classList.add('lt-access-gate-active');
    setStorefrontInert(true);
    clearError();
    gate.addEventListener('keydown', handleGateKeydown);
    requestAnimationFrame(() => codeInput.focus({ preventScroll: true }));
  }

  function closeGate() {
    if (gate.dataset.ltAccessInitialRequired === 'true') return;
    gate.hidden = true;
    document.documentElement.classList.remove('lt-access-gate-active');
    setStorefrontInert(false);
    clearError();
    codeInput.value = '';
    gate.removeEventListener('keydown', handleGateKeydown);
    if (returnFocus instanceof HTMLElement && returnFocus.isConnected) {
      if (returnFocus.hasAttribute('aria-expanded')) returnFocus.setAttribute('aria-expanded', 'false');
      returnFocus.focus({ preventScroll: true });
    }
  }

  function setBusy(busy) {
    codeInput.disabled = busy;
    submitButton.disabled = busy;
    if (publicButton) publicButton.disabled = busy;
    if (closeButton) closeButton.disabled = busy;
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
  }

  function bytesToHex(buffer) {
    return [...new Uint8Array(buffer)]
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  async function digestAccessCode(value) {
    if (!window.crypto?.subtle || !window.TextEncoder) {
      throw new Error('secure-digest-unavailable');
    }
    const encoded = new TextEncoder().encode(value);
    const digest = await window.crypto.subtle.digest('SHA-256', encoded);
    return bytesToHex(digest);
  }

  function currentServerEpoch() {
    const serverEpoch = Number.parseInt(gate.dataset.ltAccessServerNow || '', 10);
    if (!Number.isSafeInteger(serverEpoch) || serverEpoch <= 0) {
      throw new Error('invalid-server-time');
    }
    const elapsedSeconds = Math.max(0, Math.floor((performance.now() - pageStartedAt) / 1000));
    return serverEpoch + elapsedSeconds;
  }

  function normalizedAttribute(attributes, key) {
    const value = attributes?.[key];
    return value == null ? '' : String(value);
  }

  function renderedCartAccessState() {
    return {
      scope: gate.dataset.ltAccessCurrentScope || '',
      expiresAt: gate.dataset.ltAccessExpiresAt || '',
      epoch: gate.dataset.ltAccessCurrentEpoch || ''
    };
  }

  function cartAccessState(cart) {
    return {
      scope: normalizedAttribute(cart?.attributes, '_lt_scope'),
      expiresAt: normalizedAttribute(cart?.attributes, '_lt_expires_at'),
      epoch: normalizedAttribute(cart?.attributes, '_lt_epoch')
    };
  }

  function cartAccessStateMatches(left, right) {
    return left.scope === right.scope
      && left.expiresAt === right.expiresAt
      && left.epoch === right.epoch;
  }

  let cartStateRequest = null;
  async function revalidateCartAccessState() {
    if (cartStateRequest) return cartStateRequest;

    cartStateRequest = (async () => {
      try {
        const response = await fetch(gate.dataset.ltAccessCartUrl, {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) throw new Error(`cart-read-${response.status}`);

        const cart = await response.json();
        if (!cartAccessStateMatches(cartAccessState(cart), renderedCartAccessState())) {
          window.location.reload();
        }
      } catch (error) {
        console.warn('Licensed Time access state could not be revalidated.', error);
      } finally {
        cartStateRequest = null;
      }
    })();

    return cartStateRequest;
  }

  async function persistScope(scope) {
    const epoch = gate.dataset.ltAccessPolicyEpoch || '';
    if (!/^\d+$/.test(epoch)) throw new Error('invalid-policy-epoch');

    const expiresAt = String(currentServerEpoch() + ACCESS_TTL_SECONDS);
    const attributes = {
      _lt_scope: scope,
      _lt_expires_at: expiresAt,
      _lt_epoch: epoch
    };

    const response = await fetch(gate.dataset.ltAccessUpdateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ attributes })
    });

    if (!response.ok) throw new Error(`cart-update-${response.status}`);
    const cart = await response.json();
    if (
      cart?.attributes?._lt_scope !== scope
      || cart?.attributes?._lt_expires_at !== expiresAt
      || cart?.attributes?._lt_epoch !== epoch
    ) {
      throw new Error('cart-update-readback-mismatch');
    }

    accessChannel?.postMessage({ type: 'scope-updated' });
    window.location.assign(gate.dataset.ltAccessRootUrl);
  }

  async function applyScope(scope) {
    clearError();
    setBusy(true);
    try {
      await persistScope(scope);
    } catch (error) {
      console.error('Licensed Time access update failed.', error);
      setBusy(false);
      showError('Access could not be updated. Please check your connection and try again.');
    }
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    clearError();
    const accessCode = codeInput.value.trim();
    if (!accessCode) {
      showError('Enter the access code provided by Licensed Time.');
      return;
    }

    setBusy(true);
    try {
      const digest = await digestAccessCode(accessCode);
      const scope = ACCESS_SCOPE_BY_DIGEST[digest];
      if (!scope) {
        setBusy(false);
        showError('The access code was not recognized. Check the code and try again.');
        return;
      }
      await persistScope(scope);
    } catch (error) {
      console.error('Licensed Time access entry failed.', error);
      setBusy(false);
      showError('Access could not be updated. Please check your connection and try again.');
    }
  });

  codeInput.addEventListener('input', clearError);
  publicButton?.addEventListener('click', () => applyScope('public'));
  closeButton?.addEventListener('click', closeGate);

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const control = target?.closest('[data-lt-access-open]');
    if (!control) return;
    event.preventDefault();
    openGate({ initialRequired: false, trigger: control });
  }, { capture: true });

  function enforceExpiration() {
    if (gate.dataset.ltAccessInitialRequired === 'true') return;
    const expiresAt = Number.parseInt(gate.dataset.ltAccessExpiresAt || '', 10);
    if (!Number.isSafeInteger(expiresAt) || expiresAt <= 0) return;
    let secondsRemaining;
    try {
      secondsRemaining = expiresAt - currentServerEpoch();
    } catch {
      return;
    }
    if (secondsRemaining <= 0) {
      window.location.reload();
      return;
    }
    window.setTimeout(enforceExpiration, Math.min(secondsRemaining * 1000 + 250, 2147483647));
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      enforceExpiration();
      void revalidateCartAccessState();
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      window.location.reload();
      return;
    }
    void revalidateCartAccessState();
  });

  if (!gate.hidden) {
    openGate({ initialRequired: true });
  } else if (window.location.hash === '#licensed-access') {
    openGate({ initialRequired: false });
  }

  enforceExpiration();
})();
